import { createClient } from '@supabase/supabase-js'

const getEnvValue = (key) => {
  if (typeof import.meta !== 'undefined' && import.meta.env && key in import.meta.env) {
    return import.meta.env[key]
  }

  if (typeof process !== 'undefined' && process.env && key in process.env) {
    return process.env[key]
  }

  return ''
}

const invalidValues = new Set([
  '',
  'https://placeholder.supabase.co',
  'https://<PROJECT-ID>.supabase.co',
  'placeholder-key',
  '<ANON_KEY>'
])

const safeTrim = (value) => (typeof value === 'string' ? value.trim() : '')

const getStoredSettings = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }

  try {
    const stored = window.localStorage.getItem('prosper-crm-settings')
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    console.error('Failed to read stored settings:', error)
    return null
  }
}

const getSupabaseConfig = () => {
  const settings = getStoredSettings()
  const envUrl = safeTrim(getEnvValue('VITE_SUPABASE_URL') || '')
  const envKey = safeTrim(getEnvValue('VITE_SUPABASE_ANON_KEY') || '')

  const storedUrl = safeTrim(settings?.supabaseUrl || '')
  const storedKey = safeTrim(settings?.supabaseAnonKey || '')

  return {
    url: storedUrl || envUrl,
    anonKey: storedKey || envKey
  }
}

const isValidValue = (value) => Boolean(value) && !invalidValues.has(value)

// Check if Supabase credentials are configured
export const isSupabaseConfigured = () => {
  const { url, anonKey } = getSupabaseConfig()
  return isValidValue(url) && isValidValue(anonKey)
}

const createMockClient = (message) => {
  if (message) {
    console.warn(message)
  }

  return {
    from: () => ({
      select: () => ({ data: [], error: { message: 'Supabase not configured' } }),
      insert: () => ({ data: null, error: { message: 'Supabase not configured' } }),
      update: () => ({ data: null, error: { message: 'Supabase not configured' } }),
      delete: () => ({ error: { message: 'Supabase not configured' } })
    }),
    rpc: () => ({ error: { message: 'Supabase not configured' } })
  }
}

let supabaseClient = null
let lastConfigSignature = null

const initializeSupabaseClient = () => {
  const { url, anonKey } = getSupabaseConfig()
  const signature = `${url}::${anonKey}`

  if (signature === lastConfigSignature && supabaseClient) {
    return supabaseClient
  }

  lastConfigSignature = signature

  if (!isValidValue(url) || !isValidValue(anonKey)) {
    supabaseClient = createMockClient('Supabase credentials not configured. Using mock data mode.')
    return supabaseClient
  }

  try {
    supabaseClient = createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true
      }
    })
  } catch (error) {
    console.error('Error initializing Supabase:', error)
    supabaseClient = {
      from: () => ({
        select: () => ({ data: [], error: { message: 'Supabase initialization failed' } }),
        insert: () => ({ data: null, error: { message: 'Supabase initialization failed' } }),
        update: () => ({ data: null, error: { message: 'Supabase initialization failed' } }),
        delete: () => ({ error: { message: 'Supabase initialization failed' } })
      }),
      rpc: () => ({ error: { message: 'Supabase initialization failed' } })
    }
  }

  return supabaseClient
}

export const getSupabaseClient = () => initializeSupabaseClient()

const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      const client = initializeSupabaseClient()
      const value = client[prop]

      if (typeof value === 'function') {
        return value.bind(client)
      }

      return value
    },
    set(_target, prop, value) {
      const client = initializeSupabaseClient()
      client[prop] = value
      return true
    },
    has(_target, prop) {
      const client = initializeSupabaseClient()
      return prop in client
    }
  }
)

export { supabase }
export default supabase